/**
 * Shared utility for determining compile status of custom events and strategies.
 * Used across builder pages, list pages, and dashboard cards.
 */

/**
 * Get the compile status for an entity (custom event or strategy).
 * 
 * @param {Object} entity - The event or strategy object
 * @param {Object} options - Configuration options
 * @param {string} options.entityType - 'event' or 'strategy' (for message customization)
 * @returns {Object|null} - { type: 'never'|'stale', message: string } or null if compiled and up to date
 */
export const getCompileStatus = (entity, { entityType = 'event' } = {}) => {
  if (!entity) return null;

  const compiledAt = entity.metadata?.compiledAt;
  const updatedAt = entity.updatedAt;

  const entityName = entityType === 'strategy' ? 'strategy' : 'event';

  if (!compiledAt) {
    return {
      type: 'never',
      message: `This ${entityName} has never been compiled. Compile it to start ${entityType === 'strategy' ? 'generating signals' : 'detecting signals'}.`
    };
  }

  if (updatedAt && new Date(updatedAt) > new Date(compiledAt)) {
    return {
      type: 'stale',
      message: "You've made changes since last compile. Recompile to apply them."
    };
  }

  return null;
};

/**
 * Check if an entity needs compilation (for badges/chips).
 * 
 * @param {Object} entity - The event or strategy object
 * @returns {Object|null} - { needsCompile: boolean, isStale: boolean } or null if no entity
 */
export const checkNeedsCompile = (entity) => {
  if (!entity) return null;

  const compiledAt = entity.metadata?.compiledAt;
  const updatedAt = entity.updatedAt;

  if (!compiledAt) {
    return { needsCompile: true, isStale: false };
  }

  if (updatedAt && new Date(updatedAt) > new Date(compiledAt)) {
    return { needsCompile: true, isStale: true };
  }

  return { needsCompile: false, isStale: false };
};

/**
 * Get badge label for compile status.
 * 
 * @param {Object} entity - The event or strategy object
 * @returns {string|null} - 'Needs Compile', 'Needs Recompile', or null
 */
export const getCompileStatusBadge = (entity) => {
  const status = checkNeedsCompile(entity);
  if (!status || !status.needsCompile) return null;
  return status.isStale ? 'Needs Recompile' : 'Needs Compile';
};

export default {
  getCompileStatus,
  checkNeedsCompile,
  getCompileStatusBadge
};
